import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import type { CharacterDraft } from '../../stores/characterCreationStore';
import { parseCharacterJson, type ImportResult } from '../../utils/characterImport';
import type { RuntimeState } from '../../utils/characterExport';

interface ImportCharacterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (draft: CharacterDraft, runtimeState?: RuntimeState) => void;
}

export function ImportCharacterDialog({ isOpen, onClose, onImport }: ImportCharacterDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFileName(null);
      setImportResult(null);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    try {
      const text = await file.text();
      const result = parseCharacterJson(text);
      setImportResult(result);
    } catch {
      setImportResult({
        success: false,
        warnings: [],
        errors: ['Failed to read the file. Please try again.'],
      });
    }

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleImport = () => {
    if (importResult?.success && importResult.draft) {
      onImport(importResult.draft, importResult.runtimeState);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm animate-fade-in"
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          className={clsx(
            'w-full max-w-md bg-background-card rounded-xl border border-border-default',
            'shadow-2xl animate-scale-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-dialog-title"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent-gold/20 flex items-center justify-center">
                <UploadIcon className="w-5 h-5 text-accent-gold" />
              </div>
              <h2 id="import-dialog-title" className="text-lg font-semibold text-text-primary">
                Import Character
              </h2>
            </div>
            <p className="text-text-secondary">
              Select a previously exported character JSON file to import.
            </p>
          </div>

          {/* File Selection */}
          <div className="px-6 pb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              className="hidden"
            />

            <button
              type="button"
              onClick={handleFileSelect}
              className={clsx(
                'w-full px-4 py-3 rounded-lg border-2 border-dashed transition-colors',
                'flex items-center justify-center gap-2',
                fileName
                  ? 'border-accent-gold/50 bg-accent-gold/5'
                  : 'border-border-default hover:border-accent-gold/50 hover:bg-background-elevated'
              )}
            >
              <FileIcon className="w-5 h-5 text-text-muted" />
              <span className="text-text-primary font-medium">
                {fileName || 'Select JSON File'}
              </span>
            </button>
          </div>

          {/* Validation Results */}
          {importResult && (
            <div className="px-6 pb-4">
              {importResult.success ? (
                <div className="space-y-2">
                  {/* Success state */}
                  <div className="flex items-center gap-2 text-success">
                    <CheckIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Ready to import
                      {importResult.draft?.name && (
                        <span className="text-text-primary ml-1">
                          "{importResult.draft.name}"
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Runtime state indicator */}
                  {importResult.runtimeState && (
                    <div className="flex items-center gap-2 text-accent-gold">
                      <PlaySessionIcon className="w-4 h-4" />
                      <span className="text-sm">
                        Play session data included (HP, spell slots, etc.)
                      </span>
                    </div>
                  )}

                  {/* Warnings */}
                  {importResult.warnings.length > 0 && (
                    <div className="mt-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
                      <div className="flex items-start gap-2">
                        <WarningIcon className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-warning mb-1">Warnings:</p>
                          <ul className="space-y-1 text-text-secondary">
                            {importResult.warnings.map((warning, i) => (
                              <li key={i}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-error/10 border border-error/20">
                  <div className="flex items-start gap-2">
                    <ErrorIcon className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-error mb-1">Import failed:</p>
                      <ul className="space-y-1 text-text-secondary">
                        {importResult.errors.map((error, i) => (
                          <li key={i}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-default text-text-secondary font-medium hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={!importResult?.success}
              className={clsx(
                'flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors',
                importResult?.success
                  ? 'bg-accent-gold text-background-primary hover:bg-accent-gold/90'
                  : 'bg-background-elevated text-text-muted cursor-not-allowed'
              )}
            >
              Import
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function PlaySessionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
